import React, { useEffect, useMemo } from "react";
import Select from "react-select";
import { useQuery } from "@tanstack/react-query";
import { fetchCountries } from "services/dropdown";
import { DROPDOWN_STALE_TIME } from "utils/constants";
import { Controller } from "react-hook-form";

const INDIA_OPTION = { value: "IN", label: "India" };

const CountryDropDown = ({
  errors,
  control,
  setValue,
  getValues,
  watch,
  rowData,
  required,
  setCountryCode,
}) => {
  const selectedCountry = watch("country_code") || watch("country");

  const { data, isLoading } = useQuery(
    ["countries"],
    () => fetchCountries(),
    {
      keepPreviousData: false,
      staleTime: DROPDOWN_STALE_TIME,
      retry: 1,
    }
  );

  const options = useMemo(() => {
    const list = data?.data;
    const countryList = Array.isArray(list)
      ? list
      : Array.isArray(list?.data)
      ? list.data
      : Array.isArray(list?.country_data)
      ? list.country_data
      : [];

    const mapped = countryList
      .filter((item) => item?.country_code && item?.country)
      .map((item) => ({
        value: item.country_code,
        label: item.country,
      }));

    if (!mapped.some((opt) => opt.value === "IN")) {
      mapped.unshift(INDIA_OPTION);
    }
    return mapped.length ? mapped : [INDIA_OPTION];
  }, [data]);

  // Auto-set India (IN) on mount and whenever rowData changes
  useEffect(() => {
    const code = rowData?.country_code || "IN";
    const label = rowData?.country || "India";
    setValue("country_code", code);
    setValue("country", label);
    if (setCountryCode) setCountryCode(code);
  }, [rowData, setValue, setCountryCode]);

  const selectedOption =
    options.find(
      (option) =>
        option.value === (selectedCountry || rowData?.country_code || "IN") ||
        option.label === (selectedCountry || rowData?.country || "India")
    ) || INDIA_OPTION;

  return (
    <Controller
      name="country_code"
      control={control}
      defaultValue={rowData?.country_code || "IN"}
      rules={{
        required: required ? "Country is required" : false,
        validate: (value) => {
          if (required && !value) return "Country is required";
          return true;
        },
      }}
      render={({ field }) => {
        return (
          <Select
            {...field}
            options={options}
            isClearable={false}
            isLoading={isLoading}
            placeholder="Select Country"
            menuPortalTarget={document.body}
            value={selectedOption}
            onChange={(newOption) => {
              if (!newOption || newOption.value !== "IN") {
                alert(
                  "This is only available for India. You cannot change the country."
                );
                field.onChange(INDIA_OPTION.value);
                setValue("country_code", INDIA_OPTION.value);
                setValue("country", INDIA_OPTION.label);
                if (setCountryCode) setCountryCode(INDIA_OPTION.value);
                return;
              }
              field.onChange(newOption.value);
              setValue("country_code", newOption.value);
              setValue("country", newOption.label);
              if (setCountryCode) setCountryCode(newOption.value);
            }}
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
            }}
            className={`react-select-container ${
              errors?.country_code || errors?.country ? "is-invalid" : ""
            }`}
          />
        );
      }}
    />
  );
};

export default CountryDropDown;
